import React, { useState } from 'react';
import { FinancialSummary } from '../../types/data/financialRecord';
import './FinancialSummaryPanel.css';

interface FinancialSummaryPanelProps {
  summary: FinancialSummary;
}

const FinancialSummaryPanel: React.FC<FinancialSummaryPanelProps> = ({ summary }) => {
  const [isOpen, setIsOpen] = useState(true);
  
  const togglePanel = () => {
    setIsOpen(!isOpen);
  };
  
  // Format numbers with thousands separators
  const formatNumber = (value: number) => {
    return new Intl.NumberFormat('en-US').format(value);
  };

  // Get month name from number
  const getMonthName = (month: number) => {
    const months = [
      'January', 'February', 'March', 'April', 'May', 'June',
      'July', 'August', 'September', 'October', 'November', 'December'
    ];
    return months[month - 1] || `Month ${month}`;
  };
  
  return (
    <div className="summary-panel">
      <div className="summary-panel-header" onClick={togglePanel}>
        <h3>Financial Data Summary</h3>
        <button className={`toggle-button ${isOpen ? 'open' : 'closed'}`}>
          {isOpen ? '▼' : '▶'}
        </button>
      </div>
      
      {isOpen && (
        <div className="summary-panel-content">
          <div className="summary-section">
            <h4>Data Overview</h4>
            <div className="summary-stat-grid">
              <div className="summary-stat">
                <div className="stat-label">Total Records</div>
                <div className="stat-value">{formatNumber(summary.totalRecords)}</div>
              </div>
              
              <div className="summary-stat">
                <div className="stat-label">Unique Brands</div>
                <div className="stat-value">{formatNumber(summary.uniqueBrands)}</div>
              </div>
              
              <div className="summary-stat">
                <div className="stat-label">Unique Dealers</div>
                <div className="stat-value">{formatNumber(summary.uniqueDealers)}</div>
              </div>
              
              <div className="summary-stat">
                <div className="stat-label">Total KPIs</div>
                <div className="stat-value">{formatNumber(summary.totalKPIs)}</div>
              </div>
            </div>
          </div>
          
          <div className="summary-section">
            <h4>Date Range</h4>
            <div className="summary-stat-grid">
              <div className="summary-stat">
                <div className="stat-label">Start Year</div>
                <div className="stat-value">{summary.monthRange.startYear}</div>
              </div>
              
              <div className="summary-stat">
                <div className="stat-label">End Year</div>
                <div className="stat-value">{summary.monthRange.endYear}</div>
              </div>
              
              <div className="summary-stat">
                <div className="stat-label">Start Month</div>
                <div className="stat-value">{getMonthName(summary.monthRange.startMonth)}</div>
              </div>
              
              <div className="summary-stat">
                <div className="stat-label">End Month</div>
                <div className="stat-value">{getMonthName(summary.monthRange.endMonth)}</div>
              </div>
            </div>
          </div>
          
          <div className="summary-section">
            <h4>KPI Categories</h4>
            <div className="summary-stat-grid">
              {summary.kpiBreakdown.map((breakdown, index) => (
                <div key={index} className="summary-stat">
                  <div className="stat-label">{breakdown.category}</div>
                  <div className="stat-value">{formatNumber(breakdown.count)} KPIs</div>
                </div>
              ))}
            </div>
          </div>
          
          {/* Additional metrics section */}
          <div className="summary-section">
            <h4>Analytics</h4>
            <div className="summary-stat-grid">
              <div className="summary-stat">
                <div className="stat-label">Avg KPIs per Record</div>
                <div className="stat-value">
                  {summary.totalRecords > 0 
                    ? (summary.totalKPIs / summary.totalRecords).toFixed(1) 
                    : '0'}
                </div>
              </div>
              
              <div className="summary-stat">
                <div className="stat-label">Year Coverage</div>
                <div className="stat-value">
                  {summary.monthRange.endYear - summary.monthRange.startYear + 1} year{summary.monthRange.endYear - summary.monthRange.startYear !== 0 ? 's' : ''}
                </div>
              </div>
              
              <div className="summary-stat">
                <div className="stat-label">Avg Records per Dealer</div>
                <div className="stat-value">
                  {summary.uniqueDealers > 0 
                    ? (summary.totalRecords / summary.uniqueDealers).toFixed(1) 
                    : '0'}
                </div>
              </div>
              
              <div className="summary-stat">
                <div className="stat-label">Data Density</div>
                <div className="stat-value">
                  {summary.totalRecords > 0 && summary.uniqueBrands > 0 && summary.uniqueDealers > 0
                    ? `${(summary.totalRecords / (summary.uniqueBrands * summary.uniqueDealers)).toFixed(1)}x`
                    : 'N/A'}
                </div>
              </div>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default FinancialSummaryPanel; 